import { useCustom } from '@refinedev/core';
import { Table, Card, Tag, Space, Select, DatePicker, Input, Typography, Avatar } from 'antd';
import { UserOutlined, SearchOutlined } from '@ant-design/icons';
import { useState } from 'react';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface AuditLog {
  id: number;
  staffId: number;
  action: string;
  entity: string;
  entityId: number | null;
  oldValue: Record<string, unknown> | null;
  newValue: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  staff: {
    id: number;
    username: string;
    fullName: string | null;
  };
}

interface ActivityLogListProps {
  staffId?: number;
  embedded?: boolean;
  title?: string;
}

const actionLabels: Record<string, string> = {
  'staff.create': 'Xodim yaratildi',
  'staff.update': 'Xodim tahrirlandi',
  'staff.delete': "Xodim o'chirildi",
  'user.block': 'Foydalanuvchi bloklandi',
  'user.unblock': 'Foydalanuvchi blokdan chiqarildi',
  'user.update': 'Foydalanuvchi tahrirlandi',
  'driver.verify': 'Haydovchi tasdiqlandi',
  'driver.reject': 'Haydovchi rad etildi',
  'driver.create': 'Haydovchi yaratildi',
  'document.approve': 'Hujjat tasdiqlandi',
  'document.reject': 'Hujjat rad etildi',
  'trip.create': 'Sayohat yaratildi',
  'trip.cancel': 'Sayohat bekor qilindi',
  'ticket.reply': 'Murojaatga javob berildi',
  'ticket.resolve': 'Murojaat hal qilindi',
  'faq.create': 'FAQ yaratildi',
  'faq.update': 'FAQ tahrirlandi',
  'faq.delete': "FAQ o'chirildi",
  'promo.create': 'Promo kod yaratildi',
  'promo.update': 'Promo kod tahrirlandi',
  'promo.toggle': "Promo kod holati o'zgartirildi",
  'settings.update': 'Sozlamalar tahrirlandi',
};

const entityLabels: Record<string, string> = {
  Staff: 'Xodim',
  User: 'Foydalanuvchi',
  Driver: 'Haydovchi',
  Document: 'Hujjat',
  Trip: 'Sayohat',
  Ticket: 'Murojaat',
  FAQ: 'FAQ',
  PromoCode: 'Promo kod',
  Settings: 'Sozlamalar',
};

const actionColors: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  verify: 'green',
  reject: 'red',
  approve: 'green',
  block: 'red',
  unblock: 'green',
  cancel: 'orange',
  reply: 'blue',
  resolve: 'green',
  toggle: 'orange',
};

export const ActivityLogList = ({ staffId, embedded = false, title = 'Faoliyat tarixi' }: ActivityLogListProps) => {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [action, setAction] = useState<string | undefined>();
  const [entity, setEntity] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);

  const { data, isLoading } = useCustom<{ data: AuditLog[]; meta: { total: number } }>({
    url: '/admin/audit-logs',
    method: 'get',
    config: {
      query: {
        page,
        limit,
        staffId: staffId || undefined,
        action: action || undefined,
        entity: entity || undefined,
        startDate: dateRange?.[0] || undefined,
        endDate: dateRange?.[1] || undefined,
      },
    },
  });

  const logs = data?.data?.data || [];
  const total = data?.data?.meta?.total || 0;

  const getActionColor = (action: string) => {
    const parts = action.split('.');
    const actionType = parts[parts.length - 1];
    return actionColors[actionType] || 'default';
  };

  const columns = [
    {
      title: 'Vaqt',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date: string) => new Date(date).toLocaleString('uz-UZ'),
    },
    ...(staffId
      ? []
      : [
          {
            title: 'Xodim',
            dataIndex: 'staff',
            key: 'staff',
            width: 180,
            render: (staff: AuditLog['staff']) => (
              <Space>
                <Avatar size="small" icon={<UserOutlined />} />
                <Text>{staff?.fullName || staff?.username}</Text>
              </Space>
            ),
          },
        ]),
    {
      title: 'Amal',
      dataIndex: 'action',
      key: 'action',
      width: 200,
      render: (action: string) => (
        <Tag color={getActionColor(action)}>
          {actionLabels[action] || action}
        </Tag>
      ),
    },
    {
      title: "Ob'ekt",
      key: 'entity',
      width: 150,
      render: (_: unknown, record: AuditLog) => (
        <Space>
          <Text>{entityLabels[record.entity] || record.entity}</Text>
          {record.entityId && <Text type="secondary">#{record.entityId}</Text>}
        </Space>
      ),
    },
    {
      title: 'IP manzil',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 130,
      render: (ip: string | null) => ip || '-',
    },
  ];

  const content = (
    <>
      <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Select
          placeholder="Amal turi"
          allowClear
          style={{ width: 200 }}
          value={action}
          onChange={setAction}
          options={Object.entries(actionLabels).map(([value, label]) => ({
            value,
            label,
          }))}
        />
        <Select
          placeholder="Ob'ekt turi"
          allowClear
          style={{ width: 150 }}
          value={entity}
          onChange={setEntity}
          options={Object.entries(entityLabels).map(([value, label]) => ({
            value,
            label,
          }))}
        />
        <RangePicker
          placeholder={['Boshlanish', 'Tugash']}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              setDateRange([
                dates[0].format('YYYY-MM-DD'),
                dates[1].format('YYYY-MM-DD'),
              ]);
            } else {
              setDateRange(null);
            }
          }}
        />
      </div>

      <Table
        dataSource={logs}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        size="small"
        pagination={{
          current: page,
          total,
          pageSize: limit,
          onChange: setPage,
          showSizeChanger: false,
          showTotal: (total) => `Jami: ${total}`,
        }}
      />
    </>
  );

  if (embedded) {
    return <Card title={title}>{content}</Card>;
  }

  return (
    <div className="p-6">
      <Card title={title}>{content}</Card>
    </div>
  );
};
